import BigNumber from 'bignumber.js';
import { Pressable, SxProp, View } from 'dripsy';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';

import { CurrenciesContext } from '@/currencies/currency.context';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { BaseAssetSubscribedItem } from 'src/types';
import { EarnStackParamList, EarnStackRoutes } from '../../earn.navigator';
import { CurrenciesIcon, CurrencyIconVariant } from '../CurrenciesIcon';
import { PositionsProps } from '../positions/Positions.web';
import { getDaysAndHoursToMaturity } from '../../utils/getMaturityDate';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';

interface PortfolioNativeItemProps {
  item: BaseAssetSubscribedItem;
  positionsProps: PositionsProps;
  refreshPortfolio: () => void;
}

export const PortfolioNativeItem: React.FC<PortfolioNativeItemProps> = ({ item, positionsProps, refreshPortfolio }) => {
  const { value: showAmount } = useAsyncStorage<boolean>('showAmount');
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();
  const onPress = () => {
    earnNavigation.navigate(EarnStackRoutes.SubscriptionDetails, {
      subscription: item,
      onUpdate: positionsProps.onUpdate,
      refreshPortfolio: () => {}
    });
  };

  const daysAndHoursToMaturity = getDaysAndHoursToMaturity(item.maturityDate);

  return (
    <Pressable onPress={onPress} sx={styles.container}>
      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: '$1' }}>
        <CurrenciesIcon
          asset={item.asset}
          subText={item.offerName}
          variant={CurrencyIconVariant.large}
          subTextSx={{ color: '$textPrimary' }}
        />
        <TextCaption secondary sx={{ flex: 1, textAlign: 'right' }}>
          {t(daysAndHoursToMaturity.key, daysAndHoursToMaturity.options)}
        </TextCaption>
      </View>
      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', gap: '$1' }}>
        <View sx={{ alignItems: 'flex-start', gap: '$1' }}>
          <TextCaption secondary>{t('earn-invest::glossary.principle')}</TextCaption>
          <TextBody semiBold sx={{ color: '$textThemeSecondary' }}>
            {showAmount
              ? formatAmountByDecimals({
                  value: new BigNumber(item.amount).toNumber(),
                  decimals: getDecimalByAsset(item.asset),
                  suffix: item.asset
                })
              : '***************'}
          </TextBody>
        </View>
        <View sx={{ alignItems: 'flex-end', gap: '$1' }}>
          <View sx={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end', gap: '$1' }}>
            <TextCaption secondary>{t('earn-invest::glossary.interest')}</TextCaption>
            <TextCaption semiBold secondary>
              APY @ {formatAmountByDecimals({ value: item.estimatedApyPercent, decimals: 2 })}%
            </TextCaption>
          </View>
          <TextBody semiBold success>
            {showAmount
              ? formatAmountByDecimals({
                  value: item.totalRewardsAccrued,
                  decimals: getDecimalByAsset(item.asset),
                  suffix: item.asset
                })
              : '***************'}
          </TextBody>
        </View>
      </View>
    </Pressable>
  );
};

enum StyleName {
  container = 'container',
  containerTop = 'containerTop',
  containerTopRight = 'containerTopRight',
  containerBottom = 'containerBottom'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    padding: '$4',
    mb: '$4',
    borderRadius: 10,
    borderWidth: 0,
    borderColor: '$borderDivider',
    justifyContent: 'space-between',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 5
    },
    shadowOpacity: 0.08,
    shadowRadius: 15,
    elevation: 10,
    gap: '$7'
  },
  containerTop: { flexDirection: 'row', justifyContent: 'space-between' },
  containerTopRight: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: '$3' },
  containerBottom: { flexDirection: 'row', justifyContent: 'space-between' }
};
